/**
 * Synchronous report-guard snapshot for click-time navigation decisions
 * (AP-778 §7.1).
 *
 * The worklist click gesture (studyLoadGesture.ts) fires BEFORE navigation —
 * but when the worksheet has unsaved changes and autosave is off (with
 * warnings enabled), React Router's blocker will stop that navigation and
 * show the Leave/Stay modal. If the gesture had already fired, the highlight,
 * breadcrumb and overlay would jump to a study the user may decide not to
 * leave for — and the overlay would strand (a blocked navigation never
 * reaches any state that clears the gesture).
 *
 * Report publishes its guard state here every render; the rail click reads
 * it synchronously and skips the gesture exactly when the blocker will fire:
 *   - unsaved + autosave ON  → gesture + navigate; the pending save is
 *     flushed fire-and-forget by useAutoSave's unmount flush.
 *   - unsaved + autosave OFF + warn → NO gesture; the router blocker shows
 *     the modal. On "Leave", navigation proceeds down the normal
 *     (gesture-less) loading path; on "Stay", nothing has moved.
 *   - unsaved + autosave OFF + warn disabled → silent discard, by the
 *     user's explicit "don't warn" choice: gesture + navigate.
 */

export interface ReportNavigationGuard {
  studyUuid: string;
  /** True when navigating away would trigger the unsaved-changes blocker —
   *  mirrors useAutoSave's shouldBlock (unsaved ∧ warn ∧ ¬autosave ∧ ¬skip). */
  blocksNavigation: boolean;
}

let guard: ReportNavigationGuard | null = null;

export function setReportNavigationGuard(next: ReportNavigationGuard) {
  guard = next;
}

export function clearReportNavigationGuard(studyUuid: string) {
  // Only the owning report may clear — a new study's Report mounts (and
  // publishes) before the old one's unmount cleanup runs.
  if (guard?.studyUuid === studyUuid) guard = null;
}

export function getReportNavigationGuard(): ReportNavigationGuard | null {
  return guard;
}
